import { useCacheHelpers } from '../apollo/cache-helpers'
import {
  CostFragment,
  CostFragmentDoc,
  CreateCostInput,
  ProjectVersionFragment,
  ProjectVersionFragmentDoc,
  UpdateCostInput,
  useCreateCostMutation,
  useDeleteCostMutation,
  useUpdateCostMutation,
} from '../generated/graphql'

type CostMutations = {
  isCreateLoading: boolean
  isUpdateLoading: boolean
  isDeleteLoading: boolean

  createCost: (
    projectVersionId: string,
    input: CreateCostInput,
    providerId: string | null,
  ) => Promise<CostFragment>

  updateCost: (id: string, input: UpdateCostInput) => Promise<CostFragment>

  deleteCost: (costId: string, projectVersionId: string) => Promise<boolean>
}

export function useCosts(): CostMutations {
  const [createCost, { loading: isCreateLoading }] = useCreateCostMutation()
  const [updateCost, { loading: isUpdateLoading }] = useUpdateCostMutation()
  const [deleteCost, { loading: isDeleteLoading }] = useDeleteCostMutation()
  const { addItemToList, getFragment, removeItemFromList } = useCacheHelpers()

  return {
    isCreateLoading,
    isUpdateLoading,
    isDeleteLoading,

    async createCost(
      projectVersionId: string,
      input: CreateCostInput,
      providerId: string | null,
    ) {
      const result = await createCost({
        variables: {
          projectVersionId,
          input,
          providerId,
        },

        update: (_, mutationResult) => {
          const cost = mutationResult.data?.createCost
          if (!cost) {
            return
          }
          const projectVersion = getFragment<ProjectVersionFragment>(
            projectVersionId,
            ProjectVersionFragmentDoc,
          )

          addItemToList(CostFragmentDoc, cost, projectVersion, 'costs')
        },
      })

      if (!result.data) {
        throw new Error('Cost creation failed')
      }

      return result.data.createCost
    },

    async updateCost(id: string, input: UpdateCostInput) {
      const result = await updateCost({
        variables: { id, input },
      })

      if (!result.data) {
        throw new Error('Cost update failed')
      }

      return result.data.updateCost
    },

    async deleteCost(costId: string, projectVersionId: string) {
      const result = await deleteCost({
        variables: { id: costId },

        update: () => {
          const projectVersion = getFragment<ProjectVersionFragment>(
            projectVersionId,
            ProjectVersionFragmentDoc,
          )

          removeItemFromList(CostFragmentDoc, costId, projectVersion, 'costs')
        },

        optimisticResponse: {
          deleteCost: true,
        },
      })

      if (!result.data) {
        throw new Error('Cost deletion failed')
      }

      return result.data.deleteCost
    },
  }
}
